import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, ActivityIndicator } from './rn';
import { api } from '../services/api';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { getDeviceLocale } from '../utils/locale';
import { radius } from '../theme';
import { InfoButton, InfoText } from './InfoHint';

export function suggestedTranslationLanguage(codes: string[], locale: string): string | undefined {
  const normalized = locale.replace(/_/g, '-').toLowerCase();
  return codes.find(code => code.toLowerCase() === normalized)
    ?? codes.find(code => code.toLowerCase() === normalized.split('-')[0]);
}

/** Inline in contact settings: no second modal and no automatically committed default. */
export default function TranslationLanguagePicker({ token, value, busy, onSelect, onCancel }: {
  token: string; value: string | null; busy: boolean;
  onSelect: (code: string) => void; onCancel: () => void;
}) {
  const { t, lang } = useTranslation();
  const colors = useTheme();
  const [languages, setLanguages] = useState<{ code: string; name: string }[]>([]);
  const [query, setQuery] = useState('');
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(true);
  const [infoOpen, setInfoOpen] = useState(false);
  useEffect(() => {
    let active = true;
    setFailed(false);
    setLoading(true);
    api.getLanguages(token, lang).then(result => {
      if (active) setLanguages(result.languages);
    }).catch(() => { if (active) setFailed(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token, lang, retry]);
  const suggestion = suggestedTranslationLanguage(languages.map(item => item.code), getDeviceLocale());
  const choices = useMemo(() => languages.filter(item =>
    `${item.name} ${item.code}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, lang)), [languages, query, lang]);
  const row = (item: { code: string; name: string }, suggested = false) => (
    <TouchableOpacity key={`${suggested}:${item.code}`} testID={`translation-language-${suggested ? 'suggested' : item.code}`}
      accessibilityRole="button" accessibilityState={{ selected: value === item.code, disabled: busy }}
      disabled={busy} onPress={() => onSelect(item.code)}
      style={{ minHeight: 48, justifyContent: 'center', paddingVertical: 10, paddingHorizontal: 12 }}>
      <Text style={{ color: colors.black, fontSize: 16, flexShrink: 1 }}>
        {item.name}{suggested ? ` · ${t('message.languageSuggestion')}` : ''}{value === item.code ? ' ✓' : ''}
      </Text>
    </TouchableOpacity>
  );
  return <View testID="translation-language-picker" style={{ paddingVertical: 8 }}>
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <Text style={{ color: colors.black, fontSize: 16, flex: 1 }}>{t('message.chooseTranslationLanguage')}</Text>
      <InfoButton open={infoOpen} onPress={() => setInfoOpen(open => !open)} subject={t('message.translationLanguage')} />
    </View>
    <InfoText open={infoOpen} text={t('message.translationLanguageInfo')} />
    <TextInput testID="translation-language-search" value={query} onChangeText={setQuery}
      accessibilityLabel={t('common.search')} placeholder={t('common.search')}
      placeholderTextColor={colors.gray600} clearButtonMode="while-editing" autoCorrect={false}
      style={{ color: colors.black, minHeight: 48, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.gray200, borderRadius: radius.control }} />
    {loading ? <ActivityIndicator accessibilityLabel={t('common.loading')} color={colors.black} /> : failed ? <TouchableOpacity accessibilityRole="button" onPress={() => setRetry(n => n + 1)} style={{ minHeight: 48, justifyContent: 'center' }}>
      <Text style={{ color: colors.black }}>{t('common.retry')}</Text>
    </TouchableOpacity> : <ScrollView nestedScrollEnabled keyboardShouldPersistTaps="handled" style={{ maxHeight: 280 }}>
      {!query && suggestion && row(languages.find(item => item.code === suggestion)!, true)}
      {choices.map(item => row(item))}
    </ScrollView>}
    <TouchableOpacity testID="translation-language-cancel" accessibilityRole="button" disabled={busy}
      onPress={onCancel} style={{ minHeight: 48, justifyContent: 'center' }}>
      <Text style={{ color: colors.black }}>{t('common.cancel')}</Text>
    </TouchableOpacity>
  </View>;
}
